import { getFetcher } from '@/api/fetchers';
import { createKey, readKey } from '@/api/params';
import { Badge } from '@/components/Badge/Badge';
import { BooleanBadge } from '@/components/Badge/BooleanBadge';
import { ClampedTextCell } from '@/components/List/Cell/ClampedTextCell';
import { SortHeader } from '@/components/List/Header/SortHeader';
import { TextHeader } from '@/components/List/Header/TextHeader';
import { List } from '@/components/List/List';
import { type BillingsRequest } from '@/schemas/API/GET/billing/request';
import { type Billing } from '@/schemas/API/GET/billing/response';
import { Button, Input, Select, Stack, NativeSelect } from '@mantine/core';
import { type ReactElement, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { AuditModal, type ModalInfo } from './AuditModal';
import { type Report } from '@/schemas/API/GET/report/response';
import {
  type PlanningContextType,
  StateContext,
} from '@/layouts/Planning/PlanningLayout';
import { openReportsModal } from '../Modals/ReportsModal';
import { ButtonBar } from '@/components/Form/ButtonBar/ButtonBar';
import PlanningActionHandler from '@/pages/Planning/Helpers/PlanningActionHandler';
import { FormModal } from '@/components/Modal/FormModal';

const AuditNameCell = (
  isBilling: (plan: Billing | Report) => plan is Billing
) => {
  const Cell = (plan: Billing | Report) => (
    <ClampedTextCell key={plan.id}>
      {isBilling(plan)
        ? plan.client.lastName + ' ' + plan.client.firstName
        : plan.emp.lastName + ' ' + plan.emp.firstName}
    </ClampedTextCell>
  );
  return Cell;
};

const AuditStatusCell = (
  getBadgeForItemStatus: (recordState: string) => ReactElement
) => {
  const Cell = (plan: Billing | Report) => (
    <div key={`itemStatus-${plan.id}`}>
      {getBadgeForItemStatus(plan.recordState)}
    </div>
  );
  return Cell;
};

const AuditAccessCell = (plan: Billing | Report) => (
  <BooleanBadge
    key={`billingStatus-${plan.id}`}
    successLabel={'Odemčen'}
    failLabel={'Uzamčen'}
    successCond={() => !plan.locked}
  />
);

const AuditBedgeCell = (
  getBadgeForDispatch: (modState: string) => ReactElement
) => {
  const Cell = (plan: Billing | Report) => (
    <div key={`dispatchStatus-${plan.id}`}>
      {getBadgeForDispatch(plan.modState)}
    </div>
  );
  return Cell;
};

const AuditButtonCell = (openFormModal: (billingId: number) => void) => {
  const Cell = (plan: Billing | Report) => (
    <Button
      variant="outline"
      key={`generate-${plan.id}`}
      onClick={event => {
        event.stopPropagation();
        openFormModal(plan.id);
      }}
    >
      Přegenerovat podle úkolů
    </Button>
  );
  return Cell;
};

export const AuditPage = (): ReactElement => {
  const navigate = useNavigate();
  const { planId, service, month } =
    useContext<PlanningContextType>(StateContext);
  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [modalInfo, setModalInfo] = useState<ModalInfo | undefined>(undefined);
  const [requestParams, setRequestParams] = useState<BillingsRequest>({
    page: 1,
    sort: [],
    size: 10,
    locked: undefined,
  });

  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [selectedBillingId, setSelectedBillingId] = useState<number | null>(
    null
  );
  const [selectedType, setSelectedType] = useState<string>('Klienti');
  const [bulkActionModalOpened, setBulkActionModalOpened] =
    useState<boolean>(false);

  const getSelectedTypePath = (): 'billing' | 'report' =>
    selectedType === 'Klienti' ? 'billing' : 'report';

  useEffect(() => {
    const queryParams = readKey(window.location.search);
    setRequestParams(currentParams => ({
      ...currentParams,
      ...queryParams,
    }));
  }, []);

  const getDataUrl = () => {
    const baseUrl = selectedType === 'Klienti' ? '/billings' : '/reports';
    return `${baseUrl}${createKey(requestParams)}&service=${service}&month=${month.get('month') + 1}&year=${month.get('year')}`;
  };

  const { data, mutate } = useSWR<any>(getDataUrl(), getFetcher);

  useEffect(() => {
    navigate(createKey(requestParams), { replace: true });
  }, [requestParams, navigate]);

  const { data: modificationStates } = useSWR<Record<string, string>>(
    '/mappings/modification-states',
    getFetcher
  );

  const { data: recordStates } = useSWR<Record<string, string>>(
    '/mappings/record-states',
    getFetcher
  );

  const setRequestParam = <K extends keyof BillingsRequest>(
    fieldName: K,
    value: BillingsRequest[K] | undefined
  ): void => {
    setRequestParams(prevParams => {
      return { ...prevParams, [fieldName]: value };
    });
  };

  const {
    regenerateBillingOrReportForGivenId,
    regenerateBillingsOrReports,
    updateBillingOrReportLockStatus,
  } = PlanningActionHandler();

  const openFormModal = (billingId: number) => {
    setSelectedBillingId(billingId);
    setFormModalOpened(true);
  };

  const handleFormModalConfirm = async (keepManualRec: boolean) => {
    if (selectedBillingId !== null) {
      await regenerateBillingOrReportForGivenId(
        selectedBillingId,
        keepManualRec,
        getSelectedTypePath()
      );
    }
    setFormModalOpened(false);
    setSelectedBillingId(null);
    mutate(); // Re-fetch data when form modal is closed
  };

  const handleBulkActionModalConfirm = async (keepManualRec: boolean) => {
    if (planId !== undefined) {
      await regenerateBillingsOrReports(
        planId,
        keepManualRec,
        getSelectedTypePath()
      );
    }
    setBulkActionModalOpened(false);
    mutate(); // Re-fetch data when bulk action modal is closed
  };

  const onRowClick = (billingId: number, title: string) => {
    setModalInfo({
      id: billingId,
      type: getSelectedTypePath(),
      title,
    });
    setModalOpened(true);
  };

  const TypeSelector = (
    <NativeSelect
      label="Typ výkazu"
      value={selectedType}
      onChange={v => setSelectedType(v.currentTarget.value)}
      data={['Klienti', 'Zaměstnanci']}
    />
  );

  const SearchBar = (
    <Input.Wrapper label="Vyhledat">
      <Input
        placeholder="např: Václav"
        value={requestParams?.query ?? ''}
        onChange={e => setRequestParam('query', e.currentTarget.value)}
      />
    </Input.Wrapper>
  );

  const ItemStatus = (
    <Select
      label="Stav položek"
      value={requestParams.rec ?? ''}
      onChange={v => {
        setRequestParam('rec', v === '' ? undefined : (v as string));
      }}
      data={[
        '',
        ...(recordStates
          ? Object.keys(recordStates).map(key => ({
              value: key,
              label: recordStates[key],
            }))
          : []),
      ]}
    />
  );

  const lockedToString = (locked: boolean | undefined): string =>
    locked !== undefined ? 'Uzamčen' : 'Odemčen';

  const BillingState = (
    <Select
      label="Stav výkazu"
      value={
        requestParams.locked === undefined
          ? ''
          : lockedToString(requestParams.locked)
      }
      onChange={v => {
        setRequestParam('locked', v === '' ? undefined : v === 'Uzamčen');
      }}
      data={['', 'Uzamčen', 'Odemčen']}
    />
  );

  const isBilling = (plan: Billing | Report): plan is Billing => {
    return (plan as Billing).client !== undefined;
  };

  const getBadgeForDispatch = (modState: string): ReactElement => {
    const label = modificationStates?.[modState] ?? 'Neznámý';
    const colorMap: Record<string, string> = {
      ACTUAL: 'success',
      OUTDATED: 'error',
      MODIFIED: 'warning',
      NOT_DISPATCHED: 'unevaluated',
    };

    return <Badge label={label} color={colorMap[modState] ?? 'warning'} />;
  };

  const getBadgeForItemStatus = (recordState: string): ReactElement => {
    const label = recordStates?.[recordState] ?? 'Neznámý';
    const colorMap: Record<string, string> = {
      EMPTY: 'error',
      MANUAL: 'warning',
      GENERATED: 'success',
    };
    return <Badge label={label} color={colorMap[recordState] ?? 'warning'} />;
  };

  return (
    <Stack className="flex-1 overflow-auto">
      <AuditModal
        spec={modalInfo}
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          mutate(); // Re-fetch data when modal is closed
        }}
      />
      <FormModal
        opened={formModalOpened}
        onClose={() => setFormModalOpened(false)}
        onConfirm={async event => {
          await handleFormModalConfirm(true);
        }}
        onCancel={async event => {
          await handleFormModalConfirm(false);
        }}
        title="Chcete ponechat i ručně přidané záznamy?"
        confirmText="Ano"
        closeText="Zrušit"
      />
      <FormModal
        opened={bulkActionModalOpened}
        onClose={() => setBulkActionModalOpened(false)}
        onConfirm={async event => {
          await handleBulkActionModalConfirm(true);
        }}
        onCancel={async event => {
          await handleBulkActionModalConfirm(false);
        }}
        title="Chcete ponechat i ručně přidané záznamy?"
        confirmText="Ano"
        closeText="Zrušit"
      />
      <div className="flex-1 overflow-auto">
        {TypeSelector}
        <List
          pagination={{
            page: requestParams.page,
            setPage: e => setRequestParam('page', e),
            pageTotal: data?.totalPages ?? 1,
          }}
          loading={!data}
          perPageSelector={{
            label: 'Klientů na stránce',
            value: requestParams.size ?? 0,
            setter: v => setRequestParam('size', v),
          }}
          selectors={[SearchBar, ItemStatus, BillingState]}
          columns={[
            <SortHeader
              key="name"
              label="Příjmení a jméno"
              fields={['firstName', 'lastName']}
              callback={sort => setRequestParam('sort', sort)}
              currentSort={requestParams.sort ?? []}
            />,
            <TextHeader key="itemStatus">Stav položek</TextHeader>,
            <TextHeader key="billingStatus">Stav výkazu</TextHeader>,
            <TextHeader key="dispatchStatus">Odeslání</TextHeader>,
            <TextHeader key="actions">Akce</TextHeader>,
          ]}
          content={{
            items: data?.content ?? [],
            onClick: (plan: Billing | Report) =>
              onRowClick(
                plan.id,
                isBilling(plan)
                  ? plan.client.lastName + ' ' + plan.client.firstName
                  : plan.emp.lastName + ' ' + plan.emp.firstName
              ),
            renders: [
              AuditNameCell(isBilling),
              AuditStatusCell(getBadgeForItemStatus),
              AuditAccessCell,
              AuditBedgeCell(getBadgeForDispatch),
              AuditButtonCell(openFormModal),
            ],
          }}
        />
      </div>
      <ButtonBar>
        <Button onClick={() => setBulkActionModalOpened(true)}>
          Přegenerovat nezamknuté
        </Button>
        <Button
          onClick={async () => {
            if (planId !== undefined) {
              await updateBillingOrReportLockStatus(
                planId,
                getSelectedTypePath(),
                true
              );
              mutate(); // Re-fetch data after locking all
            }
          }}
        >
          Uzamknout vše
        </Button>
        <Button onClick={() => openReportsModal(planId, service, 'REPORT')}>
          Odeslat PDF
        </Button>
      </ButtonBar>
    </Stack>
  );
};

export default AuditPage;
